import type { LatLngTuple } from "leaflet"
import type { TFunction } from "i18next"

import type { GeoJSONLayersData } from "types/geolocation.types"
import type { LastLimiterBySpanId, PeakSpanMarker, SpanDatum } from "types/spans.types"

import type { LimitingSpanHighlights } from "helpers/hooks/powerlines/useLimitingSpanFrequency"
import { getIntensity } from "helpers/utils/limitingSpanColor"
import { formatLineStringCoordinates, getLineMidpoint } from "helpers/utils/map"
import type { RibbonWindowConfig } from "widgets/plots/LimitingSpansHeatmap"
import {
  needsScrubber,
  ROW_HEIGHT,
  type LaneMarkers,
  type RibbonBlockOptions,
} from "widgets/plots/LimitingSpansHeatmap/helpers"
import type { SpanGeoMeta } from "widgets/plots/powerlines/SpanSummaryCard"

/**
 * The limiting-span data is a layer on the card, not what decides it exists.
 *
 * - `collapsed` — no geometry yet, so nothing to draw.
 * - `layerLoading` — geometry is there and ampacity hasn't answered yet, or was
 *   never asked with nothing on screen. Not asking is not an answer: the range
 *   or the org is mid-change, and reading it as "no counts" flashes the neutral
 *   strip on every date change.
 * - `frequency` — the full panel. A method that failed lands here too, as a
 *   hatched lane next to one that answered.
 * - `neutral` — answered with no counts, or every method failed.
 */
export type SpanMapState = "collapsed" | "layerLoading" | "frequency" | "neutral"

interface SpanMapStateInput {
  isGeoLoading: boolean
  hasRoute: boolean
  isPending: boolean
  // The queries were never sent, so there is nothing to conclude from them.
  isSkipped: boolean
  hasCounts: boolean
  // Per method, not one flag: which lanes failed decides whether the panel has
  // anything left to draw.
  methodErrors: { aar: boolean; dlr: boolean }
}

export const getSpanMapState = ({
  isGeoLoading,
  hasRoute,
  isPending,
  isSkipped,
  hasCounts,
  methodErrors,
}: SpanMapStateInput): SpanMapState => {
  if (isGeoLoading || !hasRoute) return "collapsed"
  if (isPending) return "layerLoading"
  // Counts already on screen outlast a commit where the queries aren't asked:
  // the panel holds, with the spinner by the title, rather than blinking out.
  if (hasCounts) return "frequency"
  if (isSkipped) return "layerLoading"
  // Reached only once the queries answered, so neutral is "nothing", not "not yet".
  const failures = Object.values(methodErrors)
  // A hatched lane says something beside one that answered. Hatch every lane and
  // the panel draws nothing, so the strip falls back to the line's own spans.
  return failures.some(Boolean) && !failures.every(Boolean) ? "frequency" : "neutral"
}

// Past this the card scrolls instead.
export const MIN_MAP_HEIGHT = 180

// The card's own chrome, read by both the header and the height-budget test, so
// bumping one can't leave the card scrolling.
export const HEADER_HEIGHT = 40
export const HEADER_GAP = 16
// Between the map and the strip under it.
export const RIBBON_GAP = 8

/**
 * `layerLoading` has no rows of its own — it keeps whichever the card last
 * settled on. Neutral is both the smaller and the starting shape, so a line
 * without ampacity is never sized for a layer it won't get.
 */
export const CARD_ROWS: Record<
  Exclude<SpanMapState, "layerLoading">,
  { wide: number; narrow: number }
> = {
  collapsed: { wide: 0, narrow: 0 },
  frequency: { wide: 5, narrow: 7 },
  neutral: { wide: 4, narrow: 5 },
}

export const FLY_TO_ZOOM = 14
// The span popup opens once this is over.
export const FLY_TO_DURATION_S = 0.8
// Leaflet settles its panes a beat past the animation's nominal end.
export const FLY_TO_SETTLE_MS = 150

export const MAP_LAYERS_VISIBILITY = { Powerline: false, PowerlineSpan: true } as const

export const MOBILE_RIBBON_WINDOW: RibbonWindowConfig = {
  size: 30,
  rowHeight: 34,
  handleHitSize: 22,
  markerHitSize: 26,
  minWindow: 10,
}

// The card sizes its skeleton from this; a test checks both shapes still leave
// the map room to draw.
export const getRibbonBlocks = (
  isNarrow: boolean,
  spanCount: number,
): Record<"frequency" | "neutral", RibbonBlockOptions> => {
  const shared = {
    rowHeight: isNarrow ? MOBILE_RIBBON_WINDOW.rowHeight : ROW_HEIGHT,
    showScrubber: needsScrubber(
      spanCount,
      isNarrow ? MOBILE_RIBBON_WINDOW.size : undefined,
    ),
  }
  return {
    frequency: {
      ...shared,
      lanes: 2,
      showMarkerBands: true,
      showLaneTitle: false,
      showSummaryRow: !isNarrow,
      showLegend: true,
    },
    neutral: {
      ...shared,
      lanes: 1,
      showMarkerBands: false,
      showLaneTitle: true,
      showSummaryRow: false,
      showLegend: false,
    },
  }
}

export const getSpanDataById = (spanData: SpanDatum[]): Map<number, SpanDatum> => {
  const bySpanId = new Map<number, SpanDatum>()
  for (const datum of spanData) {
    if (datum.spanId != null) bySpanId.set(datum.spanId, datum)
  }
  return bySpanId
}

// Each method rings its own peak span, with its own label.
const PEAKS = [
  { key: "aar", labelKey: "spans.ampacity.map.peakAar" },
  { key: "dlr", labelKey: "spans.ampacity.map.peakDlr" },
] as const

interface PeakSpanMarkersInput {
  highlights: LimitingSpanHighlights
  spanDataById: Map<number, SpanDatum>
  maxPct: number
  colorFor: (intensity: number) => string
  t: TFunction
}

// The span each method limited on most, ringed in that span's color.
export const getPeakSpanMarkers = ({
  highlights,
  spanDataById,
  maxPct,
  colorFor,
  t,
}: PeakSpanMarkersInput): PeakSpanMarker[] => {
  const peakSpanIds = {
    aar: highlights.peakAarSpanId,
    dlr: highlights.peakDlrSpanId,
  }
  return PEAKS.flatMap(({ key, labelKey }) => {
    const spanId = peakSpanIds[key]
    if (spanId == null) return []
    const datum = spanDataById.get(spanId)
    return [
      {
        spanId,
        color: colorFor(getIntensity(datum?.[key].pct ?? 0, maxPct)),
        label: t(labelKey),
        dataKey: key,
        pct: datum?.[key].pct ?? 0,
        count: datum?.[key].count ?? 0,
      },
    ]
  })
}

/**
 * The span each method last reported as limiting, for the map's alert icon and
 * its popup. Read off the highlights so the icon and the ribbon's arrow can't
 * name different spans on a historic range.
 */
export const getLastLimiterBySpanId = (
  highlights: LimitingSpanHighlights,
): LastLimiterBySpanId => {
  const bySpanId: LastLimiterBySpanId = {}
  const mark = (key: "aar" | "dlr", spanId: number | null, time: string | null) => {
    if (spanId == null) return
    bySpanId[spanId] = { ...bySpanId[spanId], [key]: { time } }
  }
  mark("aar", highlights.currentAarSpanId, highlights.currentAarTime)
  mark("dlr", highlights.currentDlrSpanId, highlights.currentDlrTime)
  return bySpanId
}

// The same spans the map rings, for the ribbon's lane arrows.
export const getRibbonMarkers = (
  highlights: LimitingSpanHighlights,
): Record<"aar" | "dlr", LaneMarkers> => ({
  aar: {
    peakSpanId: highlights.peakAarSpanId,
    currentSpanId: highlights.currentAarSpanId,
    currentTime: highlights.currentAarTime,
  },
  dlr: {
    peakSpanId: highlights.peakDlrSpanId,
    currentSpanId: highlights.currentDlrSpanId,
    currentTime: highlights.currentDlrTime,
  },
})

// The fly-to target comes with the geometry, so the map is walked once.
export type SpanGeoEntry = SpanGeoMeta & { midpoint: LatLngTuple | null }
export type SpanGeoById = Map<number, SpanGeoEntry>

export const getSpanGeoById = (geoData: GeoJSONLayersData | undefined): SpanGeoById => {
  const bySpanId: SpanGeoById = new Map()
  for (const feature of geoData?.PowerlineSpan?.features ?? []) {
    const { id, name, length } = feature.properties
    bySpanId.set(id, {
      midpoint: getLineMidpoint(
        formatLineStringCoordinates(feature.geometry.coordinates),
      ),
      name,
      length,
    })
  }
  return bySpanId
}

// The denominator for the card's "{count} of {total}".
export const getSpanTotals = (spanData: SpanDatum[]): { aar: number; dlr: number } =>
  spanData.reduce(
    (acc, d) => ({ aar: acc.aar + d.aar.count, dlr: acc.dlr + d.dlr.count }),
    { aar: 0, dlr: 0 },
  )
